import PropTypes from 'prop-types';
import PaymentOptionsLoadingStatus from '../../enums/PaymentOptionsLoadingStatus';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import doMetric from '../../v2/tracking/bigQueryMetricRecorder';
import Track from '../../v2/utils/tracking';

export default function useAdyenV2LoadedHandler(props) {
  const createComponent = (type) => {
    const adyenV2CreateComponentConfig = props.adyenV2CreateComponentConfig?.[type];
    if (adyenV2CreateComponentConfig) {
      return props.adyenV2Checkout.create(type, adyenV2CreateComponentConfig);
    }
    return props.adyenV2Checkout.create(type);
  };
  
  useDidUpdateEffect(() => {
    if (props.numberOfPaymentOptions === window.start.paymentOptions.paymentOptions.length && props.adyenV2Checkout) {
      if (props.adyenV2DropinEnabled) {
        props.addAdyenV2OptionsToState([{
          name: 'dropin',
          type: 'dropin',
          isAdyenV2Option: true,
          component: createComponent('dropin'),
        }]);
        props.setPaymentOptionsLoadingStatus(PaymentOptionsLoadingStatus.LOADED);
      } else if (props.adyenV2OptionTypeToTest) {
        // Setting to use for testing all options in the returned map from useAdyenPaymentOptionsRef hook
        props.addAdyenV2OptionsToState([{
          name: props.adyenV2OptionTypeToTest,
          type: props.adyenV2OptionTypeToTest,
          isAdyenV2Option: true,
          component: createComponent(props.adyenV2OptionTypeToTest),
        }]);
        props.setPaymentOptionsLoadingStatus(PaymentOptionsLoadingStatus.LOADED);
      } else {
        let adyenPaymentOptions = [];
        let adyenStoredPaymentOptions = [];
        let storedOptionsToAddToState = [];
        let adyenStoredPaymentOptionsForMetric = [];
        try {
          adyenPaymentOptions = props.adyenV2Checkout.paymentMethodsResponse?.paymentMethods;
          adyenStoredPaymentOptions = props.adyenV2Checkout.paymentMethodsResponse?.storedPaymentMethods;
          adyenStoredPaymentOptionsForMetric = adyenStoredPaymentOptions?.map(paymentMethod => ({
            type: 'stored payment method',
            name: paymentMethod.name,
          }));
          const paymentOptions = JSON.stringify(adyenPaymentOptions);
          const storedPaymentOptions = JSON.stringify(adyenStoredPaymentOptionsForMetric);
          doMetric(
            'adyenV2',
            [
              {name: 'paymentOptionsReturned', value: paymentOptions},
              {name: 'storedPaymentOptionsReturned', value: storedPaymentOptions},
              {name: 'ShowStoredPaymentOptions', value: props.showStoredPaymentMethods},
            ],
          );
        } catch (error) {
          Track.track(`Unable to track Adyen V2 payment options returned. ${error}`);
        }
        
        if (adyenPaymentOptions && adyenPaymentOptions?.length && adyenPaymentOptions.length > 0) {
          const optionsToAddToState = props.adyenV2Checkout.paymentMethodsResponse.paymentMethods
            .reduce((options, paymentMethod) => {
              options.push({
                name: paymentMethod.name,
                type: paymentMethod.type,
                isAdyenV2Option: true,
                component: createComponent(paymentMethod.type),
              });
              return options;
            }, []);
  
          if (props.showStoredPaymentMethods) {
            if (adyenStoredPaymentOptions && adyenStoredPaymentOptions?.length && adyenStoredPaymentOptions.length > 0) {
              storedOptionsToAddToState = props.adyenV2Checkout.paymentMethodsResponse.storedPaymentMethods
                .reduce((options, paymentMethod) => {
                  options.push({
                    name: paymentMethod.name,
                    type: paymentMethod.type,
                    isAdyenV2Option: true,
                    component: createComponent(paymentMethod),
                    lastFourDigitsCardNumber: paymentMethod.lastFour,
                  });
                  return options;
                }, []);
            }
          }
          
          const combinedOptionsToAddToState = [...storedOptionsToAddToState, ...optionsToAddToState];
          
          props.addAdyenV2OptionsToState(combinedOptionsToAddToState);
  
          combinedOptionsToAddToState.forEach((adyenOption) => {
            if (props.adyenV2SubTypesRequiringGenericBillingAddress?.length
              && props.adyenV2SubTypesRequiringGenericBillingAddress.includes(adyenOption.type)) {
              props.initialiseBillingAddressForPaymentType(`AdyenV2_${adyenOption.type}`);
            }
          });
          props.setPaymentOptionsLoadingStatus(PaymentOptionsLoadingStatus.LOADED);
        } else {
          Track.error('Adyen V2 enabled but no options returned from adyen npm package');
          props.setPaymentOptionsLoadingStatus(PaymentOptionsLoadingStatus.ERROR);
        }
      }
    } else {
      props.setPaymentOptionsLoadingStatus(PaymentOptionsLoadingStatus.NOT_LOADED);
    }
  }, [props.numberOfPaymentOptions, props.adyenV2Checkout]);
  
  return null;
}

useAdyenV2LoadedHandler.propTypes = {
  numberOfPaymentOptions: PropTypes.number.isRequired,
  adyenV2OptionTypeToTest: PropTypes.string,
  adyenV2DropinEnabled: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  adyenV2Checkout: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  refMap: PropTypes.object.isRequired,
  addAdyenV2OptionsToState: PropTypes.func.isRequired,
  showStoredPaymentMethods: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  adyenV2CreateComponentConfig: PropTypes.object.isRequired,
  adyenV2SubTypesRequiringGenericBillingAddress: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialiseBillingAddressForPaymentType: PropTypes.func.isRequired,
};
